import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import styles from './SetValues.module.scss';
import InputField from '../atoms/InputField';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import TimeInputField from '../atoms/TimeInputField';

import {
  convertDeprecatedSetMetricsToNewFormat,
  getConcentricCards,
  getDefaultSetMetricsFromExercise,
  isConcentricCardSelected,
  isExerciseAutomatic,
} from '../../utils';
import { AutomaticMetricsType } from '../../constants';
import MetricSelectionDialog from './MetricSelectionDialog';
import { useSessionData } from '../../providers';

import type {
  ConcentricCard,
  TrainingSet,
  SetMetrics,
  Exercise,
} from '../../types';

export interface SetValuesProps {
  index: number;
  set: TrainingSet;
  updateSet: (newSet: TrainingSet) => void;
  deleteSet: () => void;
  exercise: Exercise;
}

const REPS_OPTIONS = [
  ...Array.from({ length: 20 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  })),
  { value: 'AMRAP', label: 'AMRAP' },
];

const EFFORT_OPTIONS = [
  { value: 'NaN', label: 'None' }, // "reset" option
  ...Array.from({ length: 20 }, (_, i) => {
    const effortValue = (i + 1) * 5;
    return {
      value: String(effortValue),
      label: `${effortValue}%`,
    };
  }),
];

const SetValues: React.FC<SetValuesProps> = ({
  index,
  set,
  updateSet,
  deleteSet,
  exercise,
}) => {
  const { gym } = useSessionData();
  const [weight, setWeight] = useState<number | string>(set.weight ?? '');
  const [reps, setReps] = useState<number | string>(
    set.isAmrap ? 'AMRAP' : set.reps ?? '',
  );
  const [duration, setDuration] = useState<number>(set.duration ?? 0);
  const [rest, setRest] = useState<number>(set.rest ?? 0);

  const [effort, setEffort] = useState<number | null>(
    typeof set.oneRMPercentage === 'number'
      ? isNaN(set.oneRMPercentage)
        ? null
        : set.oneRMPercentage
      : null,
  );

  const [metrics, setMetrics] = useState<SetMetrics | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const isAutomatic = isExerciseAutomatic(exercise, gym?.id || '');
  const concentricCards = getConcentricCards(exercise);

  useEffect(() => {
    if (set.metrics) {
      // console.log('set.metrics before: ', set.metrics);
      const normalizedMetrics = convertDeprecatedSetMetricsToNewFormat(
        set.metrics,
        exercise,
        gym?.id || '',
      );
      // console.log('normalizedMetrics after: ', normalizedMetrics);
      setMetrics(normalizedMetrics);
    }
  }, [set.metrics, exercise, gym?.id]);

  const buildCardMetrics = (card: ConcentricCard): SetMetrics | null => {
    const currentMetrics =
      metrics ?? getDefaultSetMetricsFromExercise(exercise, gym?.id || '');
    if (!currentMetrics) return null;
    return {
      ...currentMetrics,
      concentric: {
        type: AutomaticMetricsType.Tempo,
        min: card.tempo.min,
        max: card.tempo.max,
      },
      velocity: {
        ...currentMetrics.velocity,
        concentric: {
          type: AutomaticMetricsType.Velocity,
          min: card.velocity.min,
          max: card.velocity.max,
        },
      },
    };
  };

  const handleMetricSelect = (card: ConcentricCard) => {
    const newMetrics = buildCardMetrics(card);
    if (!newMetrics) return;
    setMetrics(newMetrics);
    updateSet({ ...set, metrics: newMetrics });
    setDialogOpen(false);
  };

  // Helper to get the display text for the current metric
  const getMetricDisplay = (): string => {
    const savedMetrics = set.metrics;
    if (!savedMetrics) return '-';
    const found = concentricCards.find((card) =>
      isConcentricCardSelected(card, savedMetrics),
    );
    return found ? found.range : '-';
  };

  // We'll display the numeric value if it exists, else "NaN"
  const effortValue = effort === null ? 'NaN' : String(effort);

  // const [concentric, setConcentric] = useState<Metric>;

  const handleEffortChange = (newVal: string) => {
    const numericVal = parseFloat(newVal);
    if (isNaN(numericVal)) {
      // Means user picked the "NaN" placeholder
      setEffort(null);
      updateSet({ ...set, oneRMPercentage: NaN });
    } else {
      setEffort(numericVal);
      const hasEffortCards =
        isAutomatic && concentricCards.some(({ effort }) => !!effort);
      const card = hasEffortCards
        ? concentricCards.find(
            ({ effort }) =>
              !!effort && numericVal >= effort.min && numericVal <= effort.max,
          )
        : undefined;
      const newMetrics = card ? buildCardMetrics(card) : null;
      if (hasEffortCards) setMetrics(newMetrics);
      updateSet({
        ...set,
        oneRMPercentage: numericVal,
        metrics: hasEffortCards ? newMetrics : set.metrics,
      });
    }
  };

  const handleWeightChange = (newVal: string) => {
    // Only allow digits and a single dot — strip everything else (including `-`, `e`, `+`)
    const sanitized = newVal.replace(/[^0-9.]/g, '');
    setWeight(sanitized);
    const parsed = parseFloat(sanitized);
    updateSet({ ...set, weight: isNaN(parsed) ? 0 : parsed });
  };

  const handleRepsChange = (newVal: string) => {
    setReps(newVal);
    if (newVal === 'AMRAP') {
      updateSet({ ...set, isAmrap: true, reps: null });
    } else {
      updateSet({ ...set, isAmrap: false, reps: Number(newVal) });
    }
  };

  const handleDurationChange = (newDuration: number) => {
    setDuration(newDuration);
    updateSet({ ...set, duration: Number(newDuration) });
  };

  const handleRestChange = (newRest: number) => {
    setRest(newRest);
    updateSet({ ...set, rest: Number(newRest) });
  };

  return (
    <div
      className={classNames(
        styles.setValues,
        isAutomatic ? styles.automaticSetProperties : styles.setProperties,
      )}
    >
      <div className={styles.setTitle}>SET {index + 1}</div>

      {/* Weight */}
      <InputField
        placeholder=" - "
        type="number"
        className={styles.setInput}
        inputName={`weight-${index}`}
        value={String(weight)}
        onChange={handleWeightChange}
        onKeyDown={(e) => {
          if (e.key === '-') e.preventDefault();
        }}
        inputProps={{ min: 0 }}
      />

      {/* Reps */}
      <div className={styles.repsInputWrapper}>
        <InputField
          placeholder=" - "
          type="dropdown"
          className={styles.dropDownInput}
          inputName={`reps-${index}`}
          value={String(reps)}
          onChange={handleRepsChange}
          options={REPS_OPTIONS}
        />
        {reps === 'AMRAP' && (
          <span className={styles.amrapLabel}>As Many Reps As Possible</span>
        )}
      </div>

      {/* Duration */}
      <TimeInputField
        className={styles.setInput}
        inputName={`duration-${index}`}
        value={duration}
        onBlur={handleDurationChange}
      />

      {/* Rest */}
      <TimeInputField
        className={styles.setInput}
        inputName={`rest-${index}`}
        value={rest}
        onBlur={handleRestChange}
      />

      {/*
      EFFORT dropdown in the normal row, for both canBeAutomatic or not.
      Adjust position as desired in the layout.
      */}
      <InputField
        inputName={`effort-${index}`}
        className={styles.dropDownInput}
        type="dropdown"
        placeholder="%1RM"
        value={effortValue}
        onChange={handleEffortChange}
        onReselect={handleEffortChange}
        options={EFFORT_OPTIONS} // critical for dropdown
      />

      {/** If canBeAutomatic = true => show range input */}
      {isAutomatic && (
        <div className={styles.metricBox} onClick={() => setDialogOpen(true)}>
          {getMetricDisplay()}
        </div>
      )}

      {/* Delete set */}
      <span onClick={deleteSet} className={styles.deleteSet}>
        <DeleteForeverOutlinedIcon />
      </span>

      {/* Metric Selection Dialog */}
      <MetricSelectionDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={handleMetricSelect}
        cards={concentricCards}
        // Pass current metric for default selection
        currentMetrics={set.metrics ?? undefined}
      />
    </div>
  );
};

SetValues.displayName = 'SetValues';

export default SetValues;
